import { useI18n } from '@i18n';
import { TablePagination } from '@mui/material';
import { forwardRef } from 'react';
import { TablePaginationActions } from '@/components/table/TablePaginationActions';

type Props = {
  count: number;
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  paginationOptions?: number[];
  labelRowsPerPage?: string;
  /**
   * Opts into the "jump to page" input and grouped right-aligned layout used
   * by `PTable`. Off by default so the many legacy `DataTable`-based screens
   * sharing this component keep MUI's stock pagination controls.
   */
  jumpToPage?: boolean;
};

/**
 * Default behavior for most of our tables. Recreate the same feel for all tables with this component.
 */
const TablePaginationStandard = forwardRef<HTMLDivElement, Props>(function TablePaginationStandard(
  {
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    labelRowsPerPage,
    paginationOptions = [5, 10, 25],
    jumpToPage = false,
  },
  ref,
) {
  const { t } = useI18n();

  return (
    <TablePagination
      ref={ref}
      rowsPerPageOptions={paginationOptions}
      component="div"
      count={count}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(_, page) => setPage(page)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0);
      }}
      labelRowsPerPage={labelRowsPerPage ?? t('General_Table-PerPage')}
      labelDisplayedRows={({ from, to, count }) => t('General_Table-of', { from, to, totalAmount: count })}
      ActionsComponent={jumpToPage ? TablePaginationActions : undefined}
      sx={{
        '& .MuiSelect-icon': {
          left: '30px',
        },
        ...(jumpToPage
          ? {
              // The rows-per-page control pins to the toolbar's left edge; the spacer
              // (reordered after it) absorbs the remaining space so the row range and
              // page navigation stay grouped together on the right.
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                order: 1,
              },
              '& .MuiTablePagination-spacer': {
                order: 2,
              },
              '& .MuiTablePagination-displayedRows': {
                order: 3,
              },
              '& .MuiTablePagination-actions': {
                order: 4,
              },
            }
          : {}),
      }}
    />
  );
});

export default TablePaginationStandard;
